import { useState } from "react";
import { ChevronIcon } from "@signet/ui";
import { RailwayLogo } from "./logos.tsx";
import { Meter, ProviderCard, useLiveUsage, usd, type LiveResult } from "./parts.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   Railway: what hosting has cost so far this billing period, what it is on
   course to cost, and which projects it went on. The server reads it with
   RAILWAY_API_TOKEN and caches it for two minutes.
   ────────────────────────────────────────────────────────────────────────── */

type Line = { id: string; name: string; usd: number };
type RailwayUsage = LiveResult & {
  workspace?: { id: string; name: string };
  period?: { start: string; end: string } | null;
  currentUsageUsd: number | null;
  estimatedUsageUsd: number | null;
  includedUsd: number | null;
  creditBalanceUsd: number | null;
  estimatedBillUsd: number | null;
  plan: string | null;
  breakdown: (Line & { services?: Line[] })[];
};

const DAY = 86_400_000;

/** "27 Sep to 27 Oct, 26 days left", in the browser's own date format. */
function periodLabel(period: { start: string; end: string }, now = Date.now()): string {
  const fmt = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
  const left = Math.max(0, Math.ceil((new Date(period.end).getTime() - now) / DAY));
  return `${fmt(period.start)} to ${fmt(period.end)}, ${left === 1 ? "1 day" : `${left} days`} left`;
}

function Breakdown({ rows }: { rows: RailwayUsage["breakdown"] }) {
  const [open, setOpen] = useState<string | null>(null);
  const sorted = [...rows].sort((a, b) => b.usd - a.usd);
  const max = Math.max(0, ...sorted.map((r) => r.usd));
  const bar = (value: number) => (
    <span className="signet-usage-track"><span className="signet-usage-fill" style={{ width: `${max ? (value / max) * 100 : 0}%` }} /></span>
  );
  return (
    <div className="signet-usage-rows">
      {sorted.map((project) => {
        const services = [...(project.services ?? [])].sort((a, b) => b.usd - a.usd);
        const expanded = open === project.id;
        return (
          <div key={project.id}>
            <div className="signet-usage-row">
              {services.length ? (
                <button type="button" className="signet-usage-row-name" onClick={() => setOpen(expanded ? null : project.id)} aria-expanded={expanded}>
                  <ChevronIcon size={12} direction={expanded ? "down" : "right"} />
                  <span className="truncate">{project.name}</span>
                </button>
              ) : (
                <span className="signet-usage-row-name truncate">{project.name}</span>
              )}
              {bar(project.usd)}
              <span className="tabular">{usd(project.usd)}</span>
            </div>
            {expanded
              ? services.map((service) => (
                  <div key={service.id} className="signet-usage-row sub">
                    <span className="signet-usage-row-name truncate muted">{service.name}</span>
                    {bar(service.usd)}
                    <span className="tabular muted">{usd(service.usd)}</span>
                  </div>
                ))
              : null}
          </div>
        );
      })}
    </div>
  );
}

export function RailwayCard() {
  const live = useLiveUsage<RailwayUsage>("railway", 2 * 60_000);
  return (
    <ProviderCard
      id="railway"
      title="Railway"
      logo={RailwayLogo}
      caption="Hosting for Signet and the other apps, this billing period."
      envVar="RAILWAY_API_TOKEN"
      live={live}
    >
      {(data) => {
        const figures = [
          { label: "Used so far", value: data.currentUsageUsd },
          { label: "Estimated for the period", value: data.estimatedUsageUsd },
          { label: "Estimated bill", value: data.estimatedBillUsd },
          { label: "Included", value: data.includedUsd },
          { label: "Credit balance", value: data.creditBalanceUsd },
        ].filter((f): f is { label: string; value: number } => f.value !== null && f.value !== undefined);
        const included = data.includedUsd && data.currentUsageUsd !== null ? (data.currentUsageUsd / data.includedUsd) * 100 : null;
        return (
          <div className="signet-usage-stack">
            {data.period || data.plan || data.workspace ? (
              <p className="signet-usage-meta">
                {[data.workspace?.name, data.plan ? `${data.plan} plan` : null, data.period ? periodLabel(data.period) : null].filter(Boolean).join(" · ")}
              </p>
            ) : null}
            {figures.length ? (
              <div className="signet-usage-figures">
                {figures.map((f) => (
                  <div key={f.label}><span>{f.label}</span><strong className="tabular">{usd(f.value)}</strong></div>
                ))}
              </div>
            ) : null}
            {included !== null ? <Meter label="Of the included usage" percent={included} /> : null}
            {data.breakdown?.length ? <Breakdown rows={data.breakdown} /> : <p className="faint">No usage by project yet this period.</p>}
          </div>
        );
      }}
    </ProviderCard>
  );
}
